import { useEffect, useRef, useState } from "react";
import { api } from "../../../api/apiClient";
import type { WebOrderRow, WebOrdersReportQuery } from "../../../api/types";
import { euro } from "../../finances/opcostFormat";
import { OrderNumberLink } from "./OrderNumberLink";
import { STATE_BADGE, namesText, whenText } from "./webOrdersText";

const PAGE_SIZE = 50;

/** Who did one step; "—" when nobody did, which a phone card leaves out. */
function PersonCell({ label, people }: { label: string; people: readonly { fullName: string }[] }) {
  return (
    <td className={people.length ? undefined : "wo-none"} data-label={label}>
      {namesText(people)}
    </td>
  );
}

/**
 * G-143 — every web order of the period, newest first, fulfilled and
 * cancelled included, fifty at a time. "Details" opens the order's journey.
 */
export function OrderHistoryTable({
  query,
  total,
  onOpen,
}: {
  query: WebOrdersReportQuery;
  /** How many orders the period holds, for the "Show more" count. */
  total: number;
  onOpen: (orderId: string) => void;
}) {
  const [rows, setRows] = useState<WebOrderRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { locationId, from, to } = query;
  // Bumped whenever the period or shop changes: an answer for an older query
  // (the first page or a "Show more") is dropped instead of mixed in.
  const generation = useRef(0);

  useEffect(() => {
    const mine = ++generation.current;
    setRows([]);
    setLoading(true);
    setError(null);
    api
      .listWebOrdersReportOrders({ locationId, from, to, take: PAGE_SIZE, skip: 0 })
      .then((page) => {
        if (generation.current === mine) setRows(page.rows);
      })
      .catch((e: unknown) => {
        if (generation.current === mine) setError(e instanceof Error ? e.message : "Could not load the web orders");
      })
      .finally(() => {
        if (generation.current === mine) setLoading(false);
      });
    return () => {
      generation.current += 1;
    };
  }, [locationId, from, to]);

  const loadMore = async () => {
    const mine = generation.current;
    setLoading(true);
    setError(null);
    try {
      const page = await api.listWebOrdersReportOrders({ locationId, from, to, take: PAGE_SIZE, skip: rows.length });
      if (generation.current === mine) setRows((prev) => [...prev, ...page.rows]);
    } catch (e) {
      if (generation.current === mine) setError(e instanceof Error ? e.message : "Could not load more web orders");
    } finally {
      if (generation.current === mine) setLoading(false);
    }
  };

  const remaining = Math.max(0, total - rows.length);

  return (
    <>
      {error ? <div className="alert error">{error}</div> : null}
      <div className="table-scroll">
        <table className="wo-table">
          <thead>
            <tr>
              <th>Received</th>
              <th>Order</th>
              <th>Shop</th>
              <th>Status</th>
              <th className="wo-num">Customer paid</th>
              <th>Accepted by</th>
              <th>Built by</th>
              <th>Handed over by</th>
              <th>Courier</th>
              <th>
                <span className="visually-hidden">Details</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="muted">
                  Loading...
                </td>
              </tr>
            ) : null}
            {rows.map((row) => {
              const badge = STATE_BADGE[row.state];
              return (
                <tr key={row.id}>
                  <td className="wo-nowrap" data-label="Received">{whenText(row.arrivedAt)}</td>
                  <td className="wo-cell-title">
                    <OrderNumberLink row={row} />
                  </td>
                  <td data-label="Shop">{row.shop?.name ?? "Not taken"}</td>
                  <td data-label="Status">
                    <span className={badge.className}>{badge.label}</span>
                  </td>
                  <td className="wo-num" data-label="Customer paid">{euro(row.customerPaidEur)}</td>
                  <PersonCell label="Accepted by" people={row.acceptedBy ? [row.acceptedBy] : []} />
                  <PersonCell label="Built by" people={row.builtBy} />
                  <PersonCell label="Handed over by" people={row.handedOverBy ? [row.handedOverBy] : []} />
                  <PersonCell label="Courier" people={row.courier ? [row.courier] : []} />
                  <td>
                    <button type="button" className="wo-link" onClick={() => onOpen(row.id)}>
                      Details
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {remaining > 0 && rows.length > 0 ? (
        <button type="button" className="button plain wo-more" disabled={loading} onClick={() => void loadMore()}>
          {loading ? "Loading..." : `Show ${Math.min(remaining, PAGE_SIZE)} more`}
        </button>
      ) : null}
    </>
  );
}
